import { Project } from "interfaces";

import Tag from "ui-components/Tag";
import FilterSummary from "ui-components/Data-Filters/Filter-Summary";

import * as flowConfig from "flows/driver-analysis/config";

import {
  projectIsDriverAnalysis,
  outcomeIsBinary,
  BinaryOutcome,
  ContinousOutcome,
} from "flows/driver-analysis/interfaces";

const BinaryOutcomeSummary = ({
  outcome,
  index,
  modelBuilt,
  showOutcomeType,
}: {
  outcome: BinaryOutcome;
  index: number;
  modelBuilt: boolean;
  showOutcomeType: boolean;
}) => {
  return (
    <>
      <div className="flex flex-wrap">
        <p>
          {index >= 1 && `${index}. `}
          {outcome.variable}
          {showOutcomeType && <span> (Binary Outcome)</span>}
        </p>
        {modelBuilt && <Tag>Model Built</Tag>}
      </div>
      <p>
        Rows satisfying ALL THE BELOW FILTERS are{" "}
        <span className="underline">{outcome.tag_ones} (1s)</span>, and the
        other rows are{" "}
        <span className="underline">{outcome.tag_zeros} (0s)</span>
      </p>
      <ul className="ml-2 pl-2">
        {outcome.definition.map((f, ind) => (
          <li key={ind}>
            <FilterSummary filter={f} />
          </li>
        ))}
      </ul>
    </>
  );
};

const ContinousOutcomeSummary = ({
  outcome,
  index,
  modelBuilt,
  showOutcomeType,
}: {
  outcome: ContinousOutcome;
  index: number;
  modelBuilt: boolean;
  showOutcomeType: boolean;
}) => {
  return (
    <div className="flex flex-wrap">
      <p>
        {index >= 1 && `${index}. `}
        {outcome.variable || outcome.toString()}
        {showOutcomeType && <span> (Continous Outcome)</span>}
      </p>
      {modelBuilt && <Tag>Model Built</Tag>}
    </div>
  );
};

const ContextSummary = ({ project }: { project: Project }) => {
  if (!projectIsDriverAnalysis(project)) {
    return null;
  }

  const outcomesWithModels = project.meta_data.outcomes
    .filter((o) =>
      project.workspaces.find(
        (ws) =>
          ws.meta_data[flowConfig.WORKSPACE_OUTCOME_VARIABLE] ===
          (o.variable || o.toString())
      )
    )
    .map((oc) => oc.variable || oc.toString());

  if (project.meta_data.outcomes.length === 1) {
    const outcome = project.meta_data.outcomes[0];
    return (
      <div className="m-1">
        <div>
          Outcome Variable:{" "}
          <span className="underline">
            {outcome.variable || outcome.toString()}
          </span>{" "}
          {project.flow === flowConfig.FLOW_NAME && (
            <span>({outcomeIsBinary(outcome) ? "binary" : "continous"})</span>
          )}
          {outcomeIsBinary(outcome) && (
            <>
              <p>
                Rows satisfying ALL THE BELOW FILTERS are{" "}
                <span className="underline">{outcome.tag_ones} (1s)</span>, and
                the other rows are{" "}
                <span className="underline">{outcome.tag_zeros} (0s)</span>
              </p>
              <ul className="ml-2 pl-2 list-inside list-decimal">
                {outcome.definition.map((f, ind) => (
                  <li key={ind}>
                    <FilterSummary filter={f} />
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {outcomesWithModels.includes(
          outcome.variable || outcome.toString()
        ) && (
          <div className="flex flex-wrap">
            <Tag bgColorClass="bg-primary-main" textColorClass="text-gray-100">
              Model Built
            </Tag>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="m-1">
      <p>This project has the following outcome variables:</p>
      <div className="mx-2">
        {project.meta_data.outcomes.map((outcome, ind) =>
          outcomeIsBinary(outcome) ? (
            <BinaryOutcomeSummary
              key={outcome.variable}
              outcome={outcome}
              index={ind + 1}
              modelBuilt={outcomesWithModels.includes(outcome.variable)}
              showOutcomeType={project.flow === flowConfig.FLOW_NAME}
            />
          ) : (
            <ContinousOutcomeSummary
              key={outcome.variable || outcome.toString()}
              outcome={outcome}
              index={ind + 1}
              modelBuilt={outcomesWithModels.includes(
                outcome.variable || outcome.toString()
              )}
              showOutcomeType={project.flow === flowConfig.FLOW_NAME}
            />
          )
        )}
      </div>
    </div>
  );
};

export default ContextSummary;
